import React from 'react';
import {
  Autocomplete, Box, Button, Chip, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Paper,
  Select, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import { PersonPinCircleOutlined as MyPlaceIcon } from '@mui/icons-material';
import { plural } from '../../Documents/format';
import { fmtDay } from '../../OnlineJournal/format';
import { RemainsOrigin, RemainsSelf } from '../remainsApi';
import { MissingFilter, Option, Origin, PharmacyFilterState } from '../remainsCalc';
import { buttonSx, chipSx } from '../remainsStyles';

interface Props {
  filters: PharmacyFilterState;
  regions: Option[];
  /** Аптеки зоны с координатами - точки отсчёта расстояния */
  originPharmacies: RemainsOrigin[];
  origin: Origin | null;
  /** Сохранённая геоточка оператора - «Моё место»; null - не сохранена или недоступна (selfError) */
  self: RemainsSelf | null;
  selfError: string | null;
  withContacts: boolean;
  selectedCount: number;
  onChange: (patch: Partial<PharmacyFilterState>) => void;
  onLocate: () => void;
  onOriginPharmacy: (p: RemainsOrigin | null) => void;
  onClearSelected: () => void;
  onReset: () => void;
}

const ANY_REGION = '*';
const SAVE_HINT = 'Профиль → «Сервисы и сброс кэша» → «Геолокация» (с компьютера или по QR-коду с телефона)';

/** Подсказка «Моё место»: откуда берётся точка и где её поменять */
function selfHint(self: RemainsSelf | null, selfError: string | null): string {
  if (!self) return selfError || `Геоточка не сохранена. Сохраните её: ${SAVE_HINT}`;
  const saved = [
    self.savedAt ? `сохранена ${fmtDay(self.savedAt)}` : '',
    self.accuracy ? `точность ±${self.accuracy} м` : ''
  ].filter(Boolean).join(', ');
  return `Расстояния от вашей сохранённой геоточки${self.address ? `: ${self.address}` : ''}${saved ? ` (${saved})` : ''}. Изменить: ${SAVE_HINT}`;
}

/** Фильтры списка аптек и карты (сервер повторяет их в выгрузке) и точка отсчёта расстояний */
const RemainsFilters: React.FC<Props> = ({
  filters, regions, originPharmacies, origin, self, selfError, withContacts, selectedCount,
  onChange, onLocate, onOriginPharmacy, onClearSelected, onReset
}) => {
  const originPharmacy = origin && origin.guid ? originPharmacies.find(p => p.guid === origin.guid) || null : null;
  const atMyPlace = Boolean(origin && !origin.guid);
  const hasFilters = Boolean(filters.region || filters.openNow || filters.missing || filters.radius || origin);

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel shrink>Регион</InputLabel>
          <Select
            notched displayEmpty label="Регион" value={filters.region || ANY_REGION}
            onChange={e => onChange({ region: e.target.value === ANY_REGION ? '' : String(e.target.value) })}
            MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
          >
            <MenuItem value={ANY_REGION}>Все регионы</MenuItem>
            {regions.filter(r => r.value).map(r => (
              <MenuItem key={r.value} value={r.value} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>{r.label}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{r.count} апт.</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel shrink>Данные аптеки</InputLabel>
          <Select
            notched displayEmpty label="Данные аптеки" value={filters.missing}
            onChange={e => onChange({ missing: e.target.value as MissingFilter })}
          >
            <MenuItem value="">Все аптеки</MenuItem>
            <MenuItem value="hours">Без графика работы</MenuItem>
            {withContacts && <MenuItem value="phone">Без рабочего телефона</MenuItem>}
          </Select>
        </FormControl>

        <FormControlLabel
          control={<Switch size="small" checked={filters.openNow} onChange={e => onChange({ openNow: e.target.checked })} />}
          label={<Typography sx={{ fontSize: '0.85rem' }}>Только открытые сейчас</Typography>}
          sx={{ mx: 0.5 }}
        />

        <Autocomplete
          size="small" options={originPharmacies} value={originPharmacy}
          onChange={(_, p) => onOriginPharmacy(p)}
          getOptionLabel={p => p.name} isOptionEqualToValue={(a, b) => a.guid === b.guid}
          renderOption={(props, p) => (
            <li {...props} key={p.guid}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>{p.name}</Typography>
                {p.address && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }} noWrap title={p.address}>{p.address}</Typography>}
              </Box>
            </li>
          )}
          filterOptions={(options, state) => {
            const q = state.inputValue.trim().toLowerCase();
            if (!q) return options;
            return options.filter(p => p.name.toLowerCase().indexOf(q) !== -1 || (p.address || '').toLowerCase().indexOf(q) !== -1);
          }}
          noOptionsText="Нет аптек с координатами"
          renderInput={params => (
            <TextField
              {...params} label="Расстояние от аптеки" InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              placeholder={atMyPlace && origin ? origin.label : 'Не выбрано'}
            />
          )}
          sx={{ width: 220 }}
        />

        <Tooltip title={selfHint(self, selfError)} arrow>
          {/* span - чтобы подсказка показывалась и у неактивной кнопки */}
          <span>
            <Button
              variant={atMyPlace ? 'contained' : 'outlined'} color={atMyPlace ? 'primary' : 'inherit'} size="small"
              onClick={onLocate} disabled={!self && !atMyPlace} startIcon={<MyPlaceIcon />}
              sx={{ ...buttonSx, borderColor: '#c4c4c4' }}
            >
              Моё место
            </Button>
          </span>
        </Tooltip>

        <TextField
          size="small" label="Радиус" value={filters.radius === null ? '' : String(filters.radius)} placeholder="любой"
          disabled={!origin}
          onChange={e => {
            const v = Number(e.target.value.replace(',', '.'));
            onChange({ radius: e.target.value.trim() && v > 0 ? Math.min(v, 2000) : null });
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{ endAdornment: <InputAdornment position="end">км</InputAdornment> }}
          sx={{ width: 120 }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="outlined" color="inherit" size="small" onClick={onReset} disabled={!hasFilters} sx={{ ...buttonSx, borderColor: '#c4c4c4' }}>
          Сброс
        </Button>
      </Box>

      {selectedCount > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Chip
            size="small" onDelete={onClearSelected} sx={chipSx}
            label={`Аптеки и карта - по ${selectedCount} ${plural(selectedCount, 'выбранному товару', 'выбранным товарам', 'выбранным товарам')}`}
          />
        </Box>
      )}
    </Paper>
  );
};

export default React.memo(RemainsFilters);
